import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import PromoSlideImageCard from './partials/promo-slide-image-card'
import {useIntl} from 'react-intl'
import {remCalc} from '../../../theme/_utils'
import PromoSliderIcons from './promo-slider-icon-arrow'

export const PromoSliderImage = ({heading, sliderGroup, uid}) => {
    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('PromoSlider')
    const swiperRef = useRef(null)
    const intl = useIntl()

    const [srAnnouncement, setSrAnnouncement] = useState('')

    const [isSmallTablet] = useMediaQuery(
        `(min-width: ${remCalc(450)}) and (max-width: ${remCalc(767)})`,
    )

    const slidesToView = {base: 1, sm: 1.3, md: 1.6, lg: 1.7, xl: 1.9}

    if (!sliderGroup || sliderGroup.length === 0) return null

    // swiperjs doesn't work correctly with 3 slides when loop + centeredSlides are enabled
    const slides = [...sliderGroup]
    const needsFakeSlides = slides.length === 3
    const allSlides = needsFakeSlides
        ? [...slides, ...slides.map((s) => ({...s, isFake: true}))]
        : slides

    const setBulletA11yState = (realIndex) => {
        const bullets = document.querySelectorAll(
            `#promo-slider-image-${uid} .swiper-pagination-bullet`,
        )
        bullets.forEach((bullet, idx) => {
            if (idx === realIndex) bullet.setAttribute('aria-current', 'true')
            else bullet.removeAttribute('aria-current')
        })
    }

    const announceSlide = (realIndex) => {
        const title = slides?.[realIndex]?.text_block?.title
        const paragraph = slides?.[realIndex]?.text_block.body_copy
        const imgDesc = slides?.[realIndex]?.media?.description
        const label = `${title} ${paragraph}` || imgDesc
        setSrAnnouncement(`${label ? `: ${label}` : ''}`)
    }

    return (
        <Box
            id={`promo-slider-image-${uid}`}
            position="relative"
            __css={{...swiperStyles, ...styles.container}}
            data-testid="promo-slider-image-container"
        >
            {/*This is used so the screen reader will readout the new image selected on the swiper without stealing focus from the selected controll (prev / next arrows) */}
            <Box srOnly role="status" aria-live="polite" aria-atomic="true">
                {srAnnouncement}
            </Box>

            {heading && (
                <Text as="h2" {...styles.heading}>
                    {heading}
                </Text>
            )}
            <Box
                as="button"
                type="button"
                className="swiper-button-prev"
                aria-label={intl.formatMessage({
                    id: 'label.promo.swiper.button.prev',
                    defaultMessage: 'Previous slide',
                })}
                sx={{
                    ...styles?.chevronIconContainer,
                }}
                left={isSmallTablet ? '12%' : {base: '2%', md: '17%', lg: '20%', xl: '21%'}}
            >
                <PromoSliderIcons.left
                    {...styles.chevronIcon}
                    height="100%"
                    width="100%"
                    top="unset"
                    transform="unset"
                    aria-hidden="true"
                    focusable="false"
                />
            </Box>

            <Box
                as="button"
                type="button"
                className="swiper-button-next"
                aria-label={intl.formatMessage({
                    id: 'label.promo.swiper.button.next',
                    defaultMessage: 'Next slide',
                })}
                sx={{
                    ...styles?.chevronIconContainer,
                }}
                right={isSmallTablet ? '12%' : {base: '2%', md: '17%', lg: '20%', xl: '21%'}}
            >
                <PromoSliderIcons.right
                    {...styles.chevronIcon}
                    height="100%"
                    width="100%"
                    top="unset"
                    transform="unset"
                    aria-hidden="true"
                    focusable="false"
                />
            </Box>
            <Swiper
                aria-label={intl.formatMessage({
                    id: 'label.promo.swiper.slider',
                    defaultMessage: 'Swiper',
                })}
                modules={[Navigation, Pagination, Keyboard]}
                navigation={{
                    prevEl: `#promo-slider-image-${uid} .swiper-button-prev`,
                    nextEl: `#promo-slider-image-${uid} .swiper-button-next`,
                }}
                pagination={{
                    clickable: true,
                    el: `#promo-slider-image-${uid} .promo-slider-pagination`,
                    renderBullet: (index, className) => {
                        if (index < slides.length) {
                            const changeSlideLabel = intl.formatMessage({
                                id: 'label.promo.swiper.slider.image',
                                defaultMessage: 'Change slide',
                            })
                            return `<button type="button" class="${className}" aria-label="${changeSlideLabel}" aria-controls="promo-slider-image-${uid}" data-slide-index="${index}"></button>`
                        }
                        return ''
                    },
                }}
                keyboard={{enabled: true}}
                loop={true}
                centeredSlides={true}
                tabIndex={0}
                onInit={(swiper) => {
                    swiperRef.current = swiper
                    const bullets = document.querySelectorAll(
                        `#promo-slider-image-${uid} .swiper-pagination-bullet`,
                    )
                    bullets.forEach((b) => b.setAttribute('tabindex', '0'))
                    const initialRealIndex = swiper.realIndex % slides.length
                    setBulletA11yState(initialRealIndex)
                    announceSlide(initialRealIndex)
                }}
                onSlideChange={(swiper) => {
                    const realIndex = swiper.realIndex % slides.length
                    setBulletA11yState(realIndex)
                    announceSlide(realIndex)
                }}
                breakpoints={{
                    0: {slidesPerView: slidesToView.base, spaceBetween: 0},
                    450: {slidesPerView: slidesToView.sm, spaceBetween: 16},
                    768: {slidesPerView: slidesToView.md, spaceBetween: 24},
                    1024: {slidesPerView: slidesToView.lg, spaceBetween: 75},
                    1200: {slidesPerView: slidesToView.xl, spaceBetween: 142},
                }}
            >
                {allSlides.map((slider, index) => {
                    return (
                        <SwiperSlide key={index} data-fake={slider.isFake ? 'true' : 'false'}>
                            {({isActive}) => (
                                <Box
                                    aria-hidden={!isActive}
                                    pointerEvents={isActive ? 'auto' : 'none'}
                                >
                                    <PromoSlideImageCard
                                        title={slider.text_block.title}
                                        bodyCopy={slider.text_block.body_copy}
                                        button={slider.text_block.button}
                                        url={slider?.media?.url}
                                        alt={
                                            `${slider?.text_block.title} ${slider.text_block.body_copy}` ||
                                            slider?.media?.description
                                        }
                                        images={slider.images}
                                        tabIndex={isActive ? 0 : -1}
                                    />
                                </Box>
                            )}
                        </SwiperSlide>
                    )
                })}

                <Box
                    className="promo-slider-pagination"
                    role="group"
                    aria-label={intl.formatMessage({
                        id: 'label.promo.swiper.pagination',
                        defaultMessage: 'Pagination for promo slider',
                    })}
                />
            </Swiper>
        </Box>
    )
}

PromoSliderImage.propTypes = {
    heading: PropTypes.string,
    uid: PropTypes.string,
    sliderGroup: PropTypes.arrayOf(
        PropTypes.shape({
            text_block: PropTypes.shape({
                title: PropTypes.string,
                body_copy: PropTypes.string,
                cta: PropTypes.shape({title: PropTypes.string, href: PropTypes.string}),
                is_external_url: PropTypes.bool,
            }),
            media: PropTypes.shape({
                url: PropTypes.string,
                alt: PropTypes.string,
                description: PropTypes.string,
            }),
        }),
    ),
}
